import { createSlice } from "@reduxjs/toolkit";
import { dispatch } from "../store";
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL;

// Redux slice for holiday/leave management

let initialState = {
    leaves: [],
    isLoading: false,
    isUpdating: false,
    showSuccess: false,
    showError: false,
    errorMessage: null,
    successMessage: null,
    error: null,
};

export const slice = createSlice({
    name: "holiday",
    initialState,
    reducers: {
        // Set leave data
        setLeaves(state, action) {
            state.leaves = action.payload;
            state.isLoading = false;
        },

        // Loading states
        startLoading(state) {
            state.isLoading = true;
        },

        startUpdating(state) {
            state.isUpdating = true;
        },

        stopUpdating(state) {
            state.isUpdating = false;
        },

        // Success/Error states
        setShowSuccess(state, action) {
            state.showSuccess = action.payload;
        },

        setShowError(state, action) {
            state.showError = action.payload;
        },

        setErrorMessage(state, action) {
            state.errorMessage = action.payload;
        },

        setSuccessMessage(state, action) {
            state.successMessage = action.payload;
        },

        // Update specific leave in the array
        updateLeave(state, action) {
            const updatedLeave = action.payload;
            const index = state.leaves.findIndex(leave => leave.id === updatedLeave.id);
            if (index !== -1) {
                state.leaves[index] = { ...state.leaves[index], ...updatedLeave };
            }
            state.isUpdating = false;
        },

        // Clear messages
        clearMessages(state) {
            state.showSuccess = false;
            state.showError = false;
            state.errorMessage = null;
            state.successMessage = null;
        },

        // Error handler
        hasError(state, action) {
            state.isLoading = false;
            state.isUpdating = false;
            state.error = action.payload;
            state.showError = true;
            state.errorMessage = action.payload?.response?.data?.message || 'An error occurred';
        },
    },
});

export const {
    setLeaves,
    startLoading,
    startUpdating,
    stopUpdating,
    setShowSuccess,
    setShowError,
    setErrorMessage,
    setSuccessMessage,
    updateLeave,
    clearMessages,
    hasError,
} = slice.actions;

// Async actions

export function fetchUserLeaves(userEmail, employeeID) {
    return async (dispatch) => {
        try {
            dispatch(slice.actions.startLoading());

            const response = await axios.get(
                `${API_URL}/api/ProfileHoliday/userHolidaysList`,
                {
                    params: {
                        userEmail,
                        EmployeeID: employeeID
                    },
                    headers: {
                        'Content-Type': 'application/json',
                    },
                }
            );

            dispatch(slice.actions.setLeaves(response.data));
        } catch (error) {
            console.error('Error fetching user leaves:', error);
            dispatch(slice.actions.hasError(error));
        }
    };
}

export function editLeaveRequest(leaveData, adminEmail) {
    return async (dispatch) => {
        try {
            dispatch(slice.actions.startUpdating());

            const requestData = {
                id: leaveData.id,
                startDate: leaveData.startDateISO ? leaveData.startDateISO.split('T')[0] : null,
                endDate: leaveData.endDateISO ? leaveData.endDateISO.split('T')[0] : null,
                type: leaveData.type.trim(),
                startTime: leaveData.startTime,
                endTime: leaveData.endTime,
                adminEmail: adminEmail
            };

            const response = await axios.post(
                `${API_URL}/api/AdminHoliday/EditLeaveRequest`,
                requestData,
                {
                    headers: {
                        'Content-Type': 'application/json',
                    },
                }
            );

            if (response.data.message.success) {
                // Update the leave in the store
                dispatch(slice.actions.updateLeave({
                    ...leaveData,
                    dates: formatDatesForDisplay(leaveData.startDateISO, leaveData.endDateISO),
                    startDate: requestData.startDate,
                    endDate: requestData.endDate,
                    type: requestData.type,
                    startTime: requestData.startTime,
                    endTime: requestData.endTime,
                }));

                dispatch(slice.actions.setSuccessMessage('Leave updated successfully!'));
                dispatch(slice.actions.setShowSuccess(true));
                dispatch(slice.actions.stopUpdating());

                // Auto-clear success message after 3 seconds
                setTimeout(() => {
                    dispatch(slice.actions.clearMessages());
                }, 3000);
            } else {
                throw new Error(response.data.message.items ? response.data.message.items.join(', ') : 'Failed to update leave');
            }
        } catch (error) {
            console.error('Error updating leave:', error);

            let errorMessage = 'Failed to update leave. Please try again.';

            if (error.response?.data?.message?.items) {
                errorMessage = error.response.data.message.items.join(', ');
            } else if (error.response?.data?.message) {
                errorMessage = error.response.data.message;
            } else if (error.message) {
                errorMessage = error.message;
            }

            dispatch(slice.actions.setErrorMessage(errorMessage));
            dispatch(slice.actions.setShowError(true));

            // Auto-clear error message after 5 seconds
            setTimeout(() => {
                dispatch(slice.actions.clearMessages());
            }, 5000);
        } finally {
            dispatch(slice.actions.stopUpdating());
        }
    };
}

export function cancelLeaveRequest(leaveID, userEmail) {
    return async (dispatch) => {
        try {
            dispatch(slice.actions.startUpdating());

            const response = await axios.post(
                `${API_URL}/api/ProfileHoliday/CancelingLeaveRequest`,
                {
                    userEmail: userEmail,
                    leaveID: leaveID
                },
                {
                    headers: {
                        'Content-Type': 'application/json',
                    },
                }
            );

            if (response.status === 200) {
                // Remove the cancelled leave from store or update its status
                // You might want to refetch the data instead
                dispatch(slice.actions.setSuccessMessage('Leave cancelled successfully!'));
                dispatch(slice.actions.setShowSuccess(true));

                setTimeout(() => {
                    dispatch(slice.actions.clearMessages());
                }, 3000);
            }
        } catch (error) {
            console.error('Error cancelling leave:', error);
            dispatch(slice.actions.hasError(error));

            setTimeout(() => {
                dispatch(slice.actions.clearMessages());
            }, 5000);
        }
    };
}

// Helper function to format dates for display
function formatDatesForDisplay(startISO, endISO) {
    if (!startISO || !endISO) return '';

    const dateToStr = (iso) => {
        const d = new Date(iso);
        const day = String(d.getDate()).padStart(2, '0');
        const month = String(d.getMonth() + 1).padStart(2, '0');
        const year = d.getFullYear();
        return `${day}/${month}/${year}`;
    };

    return `${dateToStr(startISO)} – ${dateToStr(endISO)}`;
}

export default slice.reducer; 